import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Stack,
  TableContainer,
  TableHead,
  TableRow,
  TableBody,
  Table,
  Icons,
  Icon,
  IconButton,
  Button,
  Link,
} from '@nextiva/next-ui-components';
import { Avatar } from '@nextiva/next-ui-containers';
import { DATE_FORMATS, formatDate } from 'utils/date-helpers';
import { RegistrationRejectedProps } from 'interface/WizardV3';
import { downloadEnablementCsv } from 'utils/downloadEnablementCsv';
import { useGetUserInfoAvatar } from 'hooks/useGetUserInfoAvatar/useGetUserInfoAvatar';
import { useWizardContextV3 } from '../CampaignDialog/WizardContextV3/WizardContextV3';
import { TableCellStyled } from '../MessagingNew.styled';
import { CompanyTypeInfo } from '../CampaignDialog/constants';
import { OTPDialog } from '../OTPDialog/OTPDialog';
import { Subtitle3Typo } from '../CampaignDialog/WizardV3.styled';

const dataTestId = 'PlatformAdminUI-Messaging-RegistrationRejected';
export function RegistrationRejected({
  declinedData,
  smsCampaignData,
  handleOpenComplianceFeesDialog,
}: RegistrationRejectedProps) {
  const { enablementInfo } = useWizardContextV3();
  const { userInfo, isNotLoading } = useGetUserInfoAvatar(enablementInfo);
  const [openOTPDialog, setOpenOTPDialog] = useState(false);
  const [disableResend2FAEmail, setDisableResend2FAEmail] = useState(false);
  const submissionDate = formatDate(
    new Date(declinedData?.[0]?.submissionDateMs),
    DATE_FORMATS.SHORT,
  );

  const isPublic = smsCampaignData?.corpAcctType === CompanyTypeInfo[1].value;
  const authVettingStatus = smsCampaignData?.authVettingStatus;
  const hasPublicBrandFailed = isPublic && authVettingStatus === 'FAILED_BRAND';
  const hasPublicEmailFailed = isPublic && authVettingStatus === 'FAILED';
  const hasPublic2faEmailIncomplete =
    isPublic && authVettingStatus === 'FAILED_2FA_INCOMPLETE';
  const showResubmitRegistration =
    hasPublicBrandFailed ||
    (!hasPublic2faEmailIncomplete && !hasPublicEmailFailed);

  return (
    <Grid container rowSpacing={2} data-testid={dataTestId}>
      <Grid container xs={12}>
        <Typography variant="subtitle2" color="#0B1029">
          Past submissions
        </Typography>
      </Grid>
      {isNotLoading && (
        <Grid item xs={12}>
          <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="a dense table">
              <TableHead>
                <TableRow>
                  <TableCellStyled align="left">Submitted by</TableCellStyled>
                  <TableCellStyled align="left">
                    Submission date
                  </TableCellStyled>
                  <TableCellStyled align="left">Status</TableCellStyled>
                  <TableCellStyled align="left">Reason</TableCellStyled>
                  <TableCellStyled align="left">{}</TableCellStyled>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCellStyled
                    component="th"
                    scope="row"
                    align="left"
                    size="small"
                  >
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Avatar
                        contact={userInfo}
                        disablePresence
                        avatarProps={{
                          size: 24,
                        }}
                      />
                      <Typography variant="body2" color="#005FEC">
                        {`${userInfo.firstName} ${userInfo.lastName}`}
                      </Typography>
                    </Stack>
                  </TableCellStyled>
                  <TableCellStyled align="left" size="small">
                    {submissionDate}
                  </TableCellStyled>
                  <TableCellStyled
                    align="left"
                    size="small"
                    data-status="rejected"
                  >
                    Rejected
                  </TableCellStyled>
                  <TableCellStyled align="left" size="small">
                    {declinedData?.map((data, index) => (
                      <Typography
                        key={`Error${index}`}
                        variant="body2"
                        marginBottom="4px"
                      >
                        {data?.errorMessage}
                      </Typography>
                    ))}
                  </TableCellStyled>
                  <TableCellStyled align="center">
                    <IconButton
                      aria-label="Menu Icon Button"
                      data-testid={`${dataTestId}-DownloadButton`}
                      onClick={() => {
                        downloadEnablementCsv({
                          json: enablementInfo,
                          submissionDate,
                          status: 'Declined',
                        });
                      }}
                    >
                      <Icon icon={Icons.download} />
                    </IconButton>
                  </TableCellStyled>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      )}
      {showResubmitRegistration && (
        <Grid
          container
          xs={12}
          justifyContent="space-between"
          paddingTop="16px"
        >
          <Grid item>
            <Subtitle3Typo>
              To enhance your chances of approval, take a moment to review your
              previous submission.
            </Subtitle3Typo>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenComplianceFeesDialog}
              data-testid={`${dataTestId}-ResubmitRegistrationButton`}
              sx={{
                '&&.MuiButton-sizeMedium': { padding: '12px 16px' },
              }}
            >
              Resubmit registration
            </Button>
          </Grid>
        </Grid>
      )}
      {hasPublic2faEmailIncomplete && (
        <Grid container xs={12} justifyContent="flex-end" paddingTop="16px">
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              disabled={disableResend2FAEmail}
              onClick={() => setOpenOTPDialog(true)}
              data-testid={`${dataTestId}-ResubmitRegistrationButton`}
              sx={{
                '&&.MuiButton-sizeMedium': { padding: '12px 16px' },
              }}
            >
              Restart email verification
            </Button>
          </Grid>
        </Grid>
      )}
      {hasPublicEmailFailed && (
        <Grid
          container
          xs={12}
          justifyContent="space-between"
          paddingTop="16px"
        >
          <Grid item>
            <Subtitle3Typo>
              Please contact support by calling <strong>800-285-7995</strong> or
              get{' '}
              <Link
                href="https://help.nextiva.com/s/login/?ec=302&startURL=%2Fs%2F"
                rel="noreferrer"
                target="_blank"
                data-testid={`${dataTestId}-OurWebsite`}
                sx={{ color: 'info.main', fontWeight: 500 }}
              >
                help
              </Link>{' '}
              online.
            </Subtitle3Typo>
          </Grid>
        </Grid>
      )}
      <OTPDialog
        corpAcctNumber={enablementInfo?.corpAcctNbr}
        open={openOTPDialog}
        variant="restartEmailVerification"
        onClose={() => setOpenOTPDialog(false)}
        values={enablementInfo?.businessEmail}
        data-testid={`${dataTestId}-OTPDialog`}
        setDisableButton={setDisableResend2FAEmail}
      />
    </Grid>
  );
}
